import { ClassNames, css, jsx } from '@emotion/core';
import { Popover } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import Media from 'react-media';

import Button from '@/src/shared/components/Button';
import Heading from '@/src/shared/components/Heading';
import useLayoutEffect from '@/src/shared/hooks/useIsomorphicLayoutEffect';
import { windowScrollTo } from '@/utils/utils';

const isBrowser = typeof window !== 'undefined';

const getSearchEleLeft = () => {
  if (!isBrowser) return 0;
  const searchNavItem = document.querySelector('[data-key=search] > div');
  const rect = searchNavItem && searchNavItem.getBoundingClientRect();
  return (rect && (rect.left + rect.right) / 2) || 375;
};

const clickBurgerMenu = async close => {
  if (!isBrowser) return;
  const selector = `.apcia-topmenu-toggle--${close ? 'open' : 'close'}`;
  const menuIcon = document.querySelector(selector);
  const event = new Event('click', { bubbles: true });
  if (menuIcon) {
    await menuIcon.dispatchEvent(event);
  }
};

const stepsData = [
  {
    headline: 'Menu',
    description: 'The streamlined menu structure organizes information by user types.',
    placement: 'bottom',
    key: 'step1',
    beforeScroll: () => windowScrollTo({ top: 0, left: 0 }),
  },
  {
    headline: 'Search',
    description:
      'Improved search has enhanced filtering capabilities and allows you to save your search parameters to re-run at the touch of a button.',
    placement: 'bottom',
    key: 'step2',
    beforeScroll: clickBurgerMenu,
  },
  {
    headline: 'My APCIA',
    description:
      'Your saved searches automatically populate the My APCIA area; if you have no saved searches you’ll see the most important APCIA news of the day.',
    placement: 'bottomLeft',
    key: 'step3',
    beforeScroll: () => clickBurgerMenu(true),
  },
  {
    headline: 'Real-Time Daily Digest',
    description:
      'Track APCIA-produced information as it is created through the Real-Time Daily Digest.',
    placement: 'bottomLeft',
    key: 'step4',
  },
  {
    headline: 'Events',
    description:
      'See upcoming APCIA events, including conference calls, webinars, and group meetings. Click to go to the Events home page where you can search meetings by type, group, date or more.',
    placement: 'bottomLeft',
    key: 'step5',
  },
  {
    headline: 'Social Media',
    description: 'Keep up with the latest postings to @TeamAPCIA here.',
    placement: 'bottomLeft',
    key: 'step6',
  },
];

const style = {
  popover: move => css`
    .ant-popover-content {
      max-width: 450px;
      ${move
        ? css`
            transform: translateY(20px);
            .ant-popover-arrow {
              transform: translateY(-10px) rotate(45deg);
            }
          `
        : null}
    }
    .ant-popover-title {
      padding: 24px 24px 20px;
      border-bottom: 0;
    }
    .ant-popover-inner-content {
      padding: 0 24px 24px;
    }
  `,
  introPopover: (index, isMD) => {
    const left = index ? getSearchEleLeft() : 0;
    let positionStyle;
    if (isMD && !index) {
      positionStyle = css`
        left: calc(100% - 360px) !important;
      `;
    } else if (isMD && index) {
      positionStyle = css`
        left: 0px !important;
        top: 100px !important;
      `;
    } else if (index) {
      positionStyle = css`
        left: ${left - 225}px !important;
      `;
    } else {
      positionStyle = css`
        left: calc(50% - 225px) !important;
      `;
    }
    return css`
      top: 80px !important;
      ${positionStyle};
      margin-top: 10px;
      position: fixed;
      ${index < 0 || index > 1
        ? css`
            display: none;
          `
        : ''}
    `;
  },
  pciPopover: pci => {
    if (pci) {
      return css`
        @media (max-width: 576px) {
          left: 12px !important;
        }
      `;
    }
    return css``;
  },
  popoverDesc: css`
    font-size: 1.6rem;
    line-height: 2.8rem;
    margin-bottom: 20px;
  `,
  popoverFooter: css`
    display: flex;
    justify-content: space-between;
  `,
  overlay: active => css`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 38, 73, 0.65);
    display: none;
    z-index: 1029;
    ${active
      ? css`
          display: block;
        `
      : null};
  `,
};

function TourStepsWrapper(props) {
  const { children, tourActive, handleEndTour } = props;
  const [active, setActive] = useState(false);
  const [activeStep, setActiveStep] = useState(null);

  function endTour() {
    setActive(false);
    setActiveStep(null);
    handleEndTour();
    clickBurgerMenu(true);
    windowScrollTo({ top: 0, left: 0 });
  }

  function handleEndTourClick() {
    endTour();
  }

  function startTour() {
    setActive(true);
    setActiveStep('step1');
  }

  useEffect(() => {
    if (tourActive) startTour();
    if (tourActive === false) endTour();
  }, [tourActive]);

  function preventFocusLoss(e) {
    const { keyCode } = e;
    switch (keyCode) {
      case 27: // esc
        endTour();
        break;
      case 9: // tab
        e.preventDefault();
        break;
      default:
        break;
    }
  }

  useLayoutEffect(() => {
    if (activeStep && isBrowser) {
      // set focus
      setTimeout(async () => {
        const tourbtn = document.getElementById(`tourbtn${activeStep}`);
        const innerEle = document.getElementById(`tour-innerele-${activeStep}`);
        if (tourbtn) {
          tourbtn.focus();
          const { beforeScroll } = stepsData.find(sd => sd.key === activeStep);
          if (beforeScroll) {
            await beforeScroll();
          }
          if (activeStep !== 'step1' && activeStep !== 'step2') {
            const halfWindowHeight = window.innerHeight / 2;
            const grandpa = tourbtn.parentNode.parentNode.parentNode;
            const offsetTop = innerEle && innerEle.offsetTop ? innerEle.offsetTop : 0;
            windowScrollTo({
              top: offsetTop - (halfWindowHeight - grandpa.offsetHeight / 2),
              left: 0,
            });
          }
          // prevent changing focus
          document.addEventListener('keydown', preventFocusLoss);
        }
      }, 24);
    } else {
      // reset focus
    }
    return () => {
      if (isBrowser) {
        document.removeEventListener('keydown', preventFocusLoss);
      }
    };
  }, [activeStep]);

  async function handleNextClick(index) {
    const nextStep = stepsData[index];
    const stepExists = Boolean(stepsData[index]);
    if (stepExists) {
      setActiveStep(nextStep.key);
      // scroll
    }
  }

  const steps = stepsData.reduce((acc, step, i) => {
    if (!i) return acc;
    acc[step.key] = function StepWrapper(_props) {
      return (
        <ClassNames>
          {cn => (
            <Popover
              placement={step.placement}
              title={
                <Heading type={3} weight="bold" theme="gray">
                  {step.headline}
                </Heading>
              }
              overlayClassName={`${cn.css` 
                ${style.popover(i > 3)};
                ${style.pciPopover(i === 2)};
              `} popover-${step.key}`}
              arrowPointAtCenter
              autoAdjustOverflow={i !== 2}
              content={
                <React.Fragment>
                  <p css={style.popoverDesc}>{step.description}</p>
                  <div css={style.popoverFooter}>
                    {i + 1 === stepsData.length ? (
                      <Button size="small" onClick={handleEndTourClick} id={`tourbtn${step.key}`}>
                        End Tour
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        onClick={() => handleNextClick(i + 1)}
                        id={`tourbtn${step.key}`}
                      >
                        Next
                      </Button>
                    )}

                    <span>
                      <b>
                        {i + 1} of {stepsData.length}
                      </b>
                    </span>
                  </div>
                </React.Fragment>
              }
              visible={active && activeStep === step.key}
            >
              {_props.children}
            </Popover>
          )}
        </ClassNames>
      );
    };
    return acc;
  }, {});

  return (
    <React.Fragment>
      <Media query="(max-width: 992px)">
        {isMD => (
          <ClassNames>
            {cn => {
              const index = stepsData.findIndex(({ key }) => key === activeStep);
              const step = index >= 0 && index < 2 ? stepsData[index] : stepsData[0];
              const mediaPlacement = index ? 'bottomLeft' : 'bottomRight';
              const placement = !isMD ? 'bottom' : mediaPlacement;
              return (
                <Popover
                  placement={placement}
                  title={
                    <Heading type={3} weight="bold" theme="gray">
                      {step.headline}
                    </Heading>
                  }
                  overlayClassName={`${cn.css` 
                    ${style.popover()};
                    ${style.introPopover(index, isMD)};
                  `} popover-${step.key}`}
                  arrowPointAtCenter
                  autoAdjustOverflow
                  content={
                    <>
                      <p css={style.popoverDesc}>{step.description}</p>
                      <div css={style.popoverFooter}>
                        <Button
                          size="small"
                          onClick={() => handleNextClick(index + 1)}
                          id={`tourbtn${step.key}`}
                        >
                          Next
                        </Button>
                        <span>
                          <b>
                            {index + 1} of {stepsData.length}
                          </b>
                        </span>
                      </div>
                    </>
                  }
                  visible={active && activeStep === step.key}
                />
              );
            }}
          </ClassNames>
        )}
      </Media>
      <div css={style.overlay(active)} aria-hidden="true" onClick={endTour} />
      {children({ steps })}
    </React.Fragment>
  );
}

TourStepsWrapper.propTypes = {
  children: PropTypes.func.isRequired,
  tourActive: PropTypes.bool,
  handleEndTour: PropTypes.func,
};

TourStepsWrapper.defaultProps = {
  tourActive: false,
  handleEndTour: () => {},
};

export default TourStepsWrapper;
